import {
  Button,
  H2,
  Paragraph,
  Spinner,
  Text,
  XStack,
  YStack,
} from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import { useShoppingList, useUpdateShoppingListItem } from '../hooks';
import type { ShoppingListItem, ShoppingModeDepartment } from '../types';
import { groupItemsByDepartment, sortForShoppingMode } from './shopping-mode-utils';

export { sortForShoppingMode, groupItemsByDepartment };

export interface ShoppingModeScreenProps {
  listId: string;
  onExit?: () => void;
  /** Pass departments for the list's store to enable department grouping. */
  departments?: ShoppingModeDepartment[];
}

export function ShoppingModeScreen({ listId, onExit, departments }: ShoppingModeScreenProps) {
  const query = useShoppingList(listId);
  const updateItem = useUpdateShoppingListItem(listId);

  if (query.isPending) {
    return (
      <Screen>
        <Spinner />
      </Screen>
    );
  }

  if (query.isError) {
    return (
      <Screen>
        <Paragraph color="$red10">Failed to load list: {query.error.message}</Paragraph>
        {onExit ? <Button onPress={onExit}>Schließen</Button> : null}
      </Screen>
    );
  }

  const list = query.data;
  const totalCount = list.items.length;
  const checkedCount = list.items.filter((i) => i.checked).length;

  const useDepartmentGroups = departments && departments.length > 0;
  const groups = useDepartmentGroups
    ? groupItemsByDepartment(list.items, departments!)
    : null;
  const flatSorted = useDepartmentGroups ? null : sortForShoppingMode(list.items);

  return (
    <Screen>
      <XStack alignItems="center" gap="$3">
        <YStack flex={1} gap="$1">
          <H2>{list.title}</H2>
          <Text fontSize="$3" opacity={0.6}>
            {checkedCount} / {totalCount} erledigt
          </Text>
        </YStack>
        {onExit ? (
          <Button size="$4" onPress={onExit}>
            Beenden
          </Button>
        ) : null}
      </XStack>

      {totalCount === 0 ? (
        <Paragraph opacity={0.7}>Diese Liste hat noch keine Artikel.</Paragraph>
      ) : groups ? (
        <YStack gap="$4">
          {groups.map(({ department, items }) => (
            <YStack key={department?.id ?? '__none__'} gap="$2">
              <Text fontSize="$3" fontWeight="700" opacity={0.5} textTransform="uppercase">
                {department?.name ?? 'Sonstiges'}
              </Text>
              {items.map((item) => (
                <ShoppingModeRow
                  key={item.id}
                  item={item}
                  onToggle={(next) =>
                    updateItem.mutate({ itemId: item.id, dto: { checked: next } })
                  }
                />
              ))}
            </YStack>
          ))}
        </YStack>
      ) : (
        <YStack gap="$2">
          {flatSorted!.map((item) => (
            <ShoppingModeRow
              key={item.id}
              item={item}
              onToggle={(next) =>
                updateItem.mutate({ itemId: item.id, dto: { checked: next } })
              }
            />
          ))}
        </YStack>
      )}
    </Screen>
  );
}

interface ShoppingModeRowProps {
  item: ShoppingListItem;
  onToggle: (next: boolean) => void;
}

function ShoppingModeRow({ item, onToggle }: ShoppingModeRowProps) {
  const showQuantity = item.quantity > 1;
  return (
    <XStack
      alignItems="center"
      gap="$4"
      paddingVertical="$4"
      paddingHorizontal="$4"
      borderRadius="$4"
      borderWidth={1}
      borderColor={item.missing ? '$orange9' : '$borderColor'}
      backgroundColor={
        item.checked ? '$color2' : item.missing ? '$orange3' : '$background'
      }
      opacity={item.checked ? 0.5 : 1}
      onPress={() => onToggle(!item.checked)}
      pressStyle={{ backgroundColor: '$color4' }}
      cursor="pointer"
      minHeight={72}
    >
      <Text fontSize="$8" fontWeight="700" width={40} textAlign="center">
        {item.checked ? '✓' : item.missing ? '⚠' : '○'}
      </Text>
      <YStack flex={1} gap="$1">
        <Text
          fontSize="$7"
          fontWeight="600"
          textDecorationLine={item.checked ? 'line-through' : 'none'}
        >
          {showQuantity ? `${item.quantity}× ${item.title}` : item.title}
        </Text>
        {item.description ? (
          <Text fontSize="$3" opacity={0.7}>
            {item.description}
          </Text>
        ) : null}
      </YStack>
    </XStack>
  );
}

